import { Command } from "commander";
import {
  writeFileSync,
  mkdirSync,
  existsSync,
  readFileSync,
  readdirSync,
} from "node:fs";
import { resolve, dirname } from "node:path";
import { fileURLToPath } from "node:url";
import { execSync } from "node:child_process";
import chalk from "chalk";
import { confirm } from "@inquirer/prompts";
import { scaffoldProject, detectProjectName, detectStack } from "./setup.js";

export const initCommand = new Command("init")
  .description("Initialize Fathom: create global config and scaffold project directory")
  .option("--convex-url <url>", "Convex deployment URL")
  .option("--team <name>", "Team name")
  .option("--admin-key <key>", "Anthropic Admin API key (sk-ant-admin...)")
  .option("--project <name>", "Project name (auto-detected from package.json if omitted)")
  .option("--project-dir <dir>", "Project directory (defaults to current directory)")
  .option("--quick", "Auto-detect and initialize with zero prompts")
  .action(async (options: {
    convexUrl?: string;
    team?: string;
    adminKey?: string;
    project?: string;
    projectDir?: string;
    quick?: boolean;
  }) => {
    const projectDir = options.projectDir
      ? resolve(options.projectDir)
      : process.cwd();

    const projectName = options.project ?? detectProjectName(projectDir);

    // Quick init: auto-detect everything, zero prompts
    if (options.quick) {
      const stack = detectStack(projectDir);

      console.log(chalk.bold(`\nFathom — Quick Init: ${projectName}`));
      console.log(chalk.dim("─".repeat(50)));

      // Scaffold project directory
      scaffoldProject(projectName, { projectDir });

      // Write minimal intent.yaml with detected stack
      const fathomDir = resolve(projectDir, ".fathom");
      mkdirSync(fathomDir, { recursive: true });
      const intentYaml = [
        `project: ${projectName}`,
        `description: "Auto-initialized by fathom init --quick"`,
        `tech:`,
        `  stack:`,
        ...stack.map((s) => `    - ${s}`),
        "",
      ].join("\n");
      writeFileSync(resolve(fathomDir, "intent.yaml"), intentYaml);

      const stackStr = stack.length > 0 ? stack.join(", ") : "none detected";
      console.log(chalk.green(`  ✓ intent.yaml (stack: ${stackStr})`));
      console.log(chalk.bold(`\n✓ Initialized "${projectName}" with stack: ${stackStr}`));
      console.log(chalk.dim("  Next: git add .fathom && git commit\n"));
      return;
    }

    console.log(chalk.bold(`\nFathom — Init: ${projectName}`));
    console.log(chalk.dim("─".repeat(50)));

    // 1. Global config (existing behavior)
    const configDir = resolve(
      process.env.HOME ?? process.env.USERPROFILE ?? ".",
      ".config",
      "fathom",
    );
    mkdirSync(configDir, { recursive: true });

    const configPath = resolve(configDir, "config.json");
    let existing: Record<string, unknown> = {};
    if (existsSync(configPath)) {
      try {
        existing = JSON.parse(readFileSync(configPath, "utf-8"));
      } catch {
        // ignore parse errors
      }
    }

    const config = {
      ...existing,
      ...(options.convexUrl && { convexUrl: options.convexUrl }),
      ...(options.team && { team: options.team }),
      ...(options.adminKey && { anthropicAdminKey: options.adminKey }),
      updatedAt: new Date().toISOString(),
    };

    writeFileSync(configPath, JSON.stringify(config, null, 2));
    console.log(chalk.green(`  ✓ Global config: ${configPath}`));

    if (options.convexUrl) {
      console.log(chalk.dim(`    Convex URL: ${options.convexUrl}`));
    }
    if (options.team) {
      console.log(chalk.dim(`    Team: ${options.team}`));
    }

    // 2. Project scaffold (new behavior)
    console.log("");
    scaffoldProject(projectName, { projectDir });

    // 3. Offer Claude Code plugin install if Claude Code is detected
    let claudeDetected = false;
    try {
      execSync("which claude", { stdio: "ignore" });
      claudeDetected = true;
    } catch {
      // Claude Code not on PATH
    }

    if (claudeDetected) {
      console.log("");
      console.log(chalk.cyan("  Claude Code detected!"));
      const installPlugin = await confirm({
        message:
          "Install Fathom plugin for Claude Code? (adds skills + MCP config)",
        default: true,
      });

      if (installPlugin) {
        const __dirname = dirname(fileURLToPath(import.meta.url));
        // Try npm-installed path first (plugins copied by tsup), then monorepo dev path
        let pluginSource = resolve(__dirname, "../plugins/fathom");
        if (!existsSync(pluginSource)) {
          pluginSource = resolve(__dirname, "../../plugins/fathom");
        }
        const skillsSource = resolve(pluginSource, "skills");

        if (existsSync(skillsSource)) {
          const skillDirs = readdirSync(skillsSource, { withFileTypes: true })
            .filter((d) => d.isDirectory())
            .map((d) => d.name);

          for (const skillDir of skillDirs) {
            const src = resolve(skillsSource, skillDir, "SKILL.md");
            if (!existsSync(src)) continue;
            const destDir = resolve(
              projectDir,
              ".claude",
              "skills",
              "fathom",
              skillDir,
            );
            mkdirSync(destDir, { recursive: true });
            writeFileSync(
              resolve(destDir, "SKILL.md"),
              readFileSync(src, "utf-8"),
            );
          }
          console.log(chalk.green("  \u2713 Claude Code skills installed"));
        }

        // Copy plugin.json
        const pluginJsonSrc = resolve(
          pluginSource,
          ".claude-plugin",
          "plugin.json",
        );
        if (existsSync(pluginJsonSrc)) {
          const destDir = resolve(projectDir, ".claude-plugin");
          mkdirSync(destDir, { recursive: true });
          writeFileSync(
            resolve(destDir, "plugin.json"),
            readFileSync(pluginJsonSrc, "utf-8"),
          );
          console.log(chalk.green("  \u2713 Plugin manifest installed"));
        }
      }
    }

    console.log(chalk.bold(`\n\u2713 Fathom initialized for "${projectName}"`));
    console.log(chalk.dim("  Next: git add .fathom && git commit"));
    console.log(chalk.dim("  Then: fathom analyze  (to build feature registry)\n"));
  });
